import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpClientModule, HttpParams } from '@angular/common/http';
import { Observable, of, combineLatest } from 'rxjs';
import { CommonModule } from '@angular/common';
import { forEach, isObject, isArray, map as map$1, isUndefined } from 'lodash';
import { map } from 'rxjs/operators';

const API_CONFIG = new InjectionToken("api.config");
const apiBaseConfig = {
    apiBaseUrl: "https://api.eslgaming.com",
    drupalBaseUrl: "https://play.eslgaming.com/xapi",
    playBaseUrl: "https://api.eslgaming.com/play/v1",
    giBaseUrl: "https://api.eslgaming.com/gi/v1",
    metarankBaseUrl: "https://api.eslgaming.com/metarank/v1/",
};

class ApiError extends Error {
    constructor(errorCode, message, errors) {
        super(message);
        this.errorCode = errorCode;
        this.message = message;
        this.errors = errors;
    }
}

class AuthenticationError extends ApiError {
    constructor(message) {
        super(401, message);
        this.message = message;
    }
}

class AuthorizationError extends ApiError {
    constructor(message) {
        super(403, message);
        this.message = message;
    }
}

class InternalServerError extends ApiError {
    constructor(message) {
        super(500, message);
        this.message = message;
    }
}

class MaintenanceError extends ApiError {
    constructor(message) {
        super(503, message);
        this.message = message;
    }
}

class BadRequestError extends ApiError {
    constructor(message, errors) {
        super(400, message, errors);
        this.message = message;
        this.errors = errors;
    }
}

class NotFoundError extends ApiError {
    constructor(message) {
        super(404, message);
        this.message = message;
    }
}

function apiError() {
    return (source) => {
        return new Observable(observer => {
            const obs = {
                next: x => observer.next(x),
                error: (error) => {
                    let apiErrorObj;
                    /**
                     * Sometimes it's a json string and sometimes an object
                     */
                    try {
                        apiErrorObj = JSON.parse(error.error);
                    }
                    catch (e) {
                        apiErrorObj = error.error;
                    }
                    const status = error.status ? error.status : 0;
                    let err = new ApiError(status || 0, apiErrorObj ? apiErrorObj.error : "Unknown Error");
                    switch (status) {
                        case 400:
                            err = new BadRequestError(apiErrorObj ? apiErrorObj.error : "Unknown Error", apiErrorObj ? (apiErrorObj.errors ? apiErrorObj.errors : []) : []);
                            break;
                        case 401:
                            err = new AuthenticationError(apiErrorObj ? apiErrorObj.error : "Unknown Error");
                            break;
                        case 403:
                            err = new AuthorizationError(apiErrorObj ? apiErrorObj.error : "Unknown Error");
                            break;
                        case 404:
                            err = new NotFoundError(apiErrorObj ? apiErrorObj.error : "Unknown Error");
                            break;
                        case 500:
                            err = new InternalServerError(apiErrorObj ? apiErrorObj.error : "Unknown Error");
                            break;
                        case 503:
                            err = new MaintenanceError(apiErrorObj ? apiErrorObj.error : "Unknown Error");
                            break;
                    }
                    observer.error(err);
                },
                complete: () => observer.complete(),
            };
            return source.subscribe(obs);
        });
    };
}

class ContestantResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Signs up the specified contestant to a tournament
     * To sign up a team the user must be a manager of that team
     *
     * @param leagueId
     * @param contestantId
     */
    signUpToLeague(leagueId, contestantId) {
        return this.http
            .post(`${this.config.playBaseUrl}/leagues/${leagueId}/signup`, {
            contestantId: contestantId,
        })
            .pipe(apiError());
    }
    /**
     * Checks in the specified contestant to a tournament
     * Every teammember can check a team in
     *
     * @param leagueId
     * @param contestantId
     */
    checkInToLeague(leagueId, contestantId) {
        return this.http
            .post(`${this.config.playBaseUrl}/leagues/${leagueId}/checkin`, {
            contestantId: contestantId,
        })
            .pipe(apiError());
    }
    /**
     * Signs the specified contestant off from the tournament
     * To sign off a team the user must be a manager of that team
     *
     * @param leagueId
     * @param contestantId
     */
    signOffFromLeague(leagueId, contestantId) {
        return this.http
            .post(`${this.config.playBaseUrl}/leagues/${leagueId}/signoff`, {
            contestantId: contestantId,
        })
            .pipe(apiError());
    }
}
ContestantResource.ɵfac = function ContestantResource_Factory(t) { return new (t || ContestantResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
ContestantResource.ɵprov = i0.ɵɵdefineInjectable({ token: ContestantResource, factory: ContestantResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ContestantResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class ContestantApiModule {
}
ContestantApiModule.ɵfac = function ContestantApiModule_Factory(t) { return new (t || ContestantApiModule)(); };
ContestantApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: ContestantApiModule });
ContestantApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [ContestantResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(ContestantApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ContestantApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [ContestantResource],
            }]
    }], null, null); })();

function deepFreeze(obj) {
    forEach(obj, prop => {
        if (isObject(prop) || isArray(prop)) {
            deepFreeze(prop);
        }
    });
    return isObject(obj) ? Object.freeze(obj) : obj;
}
function immutable() {
    return (source) => {
        return new Observable(observer => source.subscribe({
            // next: value => observer.next(deepFreeze(value)),
            next: x => observer.next(x),
            error: x => observer.error(x),
            complete: () => observer.complete(),
        }));
    };
}

class GameResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Fetches the name of a videogame by the games id.
     * Do not confuse a game's id with a GameId which is a users gameaccount
     *
     * @param gameId
     */
    getGame(gameId) {
        return this.http.get(`${this.config.playBaseUrl}/games/${gameId}`).pipe(immutable(), apiError());
    }
}
GameResource.ɵfac = function GameResource_Factory(t) { return new (t || GameResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
GameResource.ɵprov = i0.ɵɵdefineInjectable({ token: GameResource, factory: GameResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class GameApiModule {
}
GameApiModule.ɵfac = function GameApiModule_Factory(t) { return new (t || GameApiModule)(); };
GameApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: GameApiModule });
GameApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [GameResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(GameApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [GameResource],
            }]
    }], null, null); })();

class GameaccountTypeResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Fetches all gameaccountType descriptions
     */
    getDescriptions() {
        return this.http.get(`${this.config.drupalBaseUrl}/gameaccounts`).pipe(immutable(), apiError());
    }
    /**
     * Fetches the gameaccount provider (e.g. 'psn', 'bethesda') for a gameaccount type
     *
     * @param type
     */
    getProviderOfType(type) {
        return this.http.get(`${this.config.playBaseUrl}/gameaccounts/${type}`).pipe(immutable(), apiError());
    }
}
GameaccountTypeResource.ɵfac = function GameaccountTypeResource_Factory(t) { return new (t || GameaccountTypeResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
GameaccountTypeResource.ɵprov = i0.ɵɵdefineInjectable({ token: GameaccountTypeResource, factory: GameaccountTypeResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameaccountTypeResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class GameaccountTypeApiModule {
}
GameaccountTypeApiModule.ɵfac = function GameaccountTypeApiModule_Factory(t) { return new (t || GameaccountTypeApiModule)(); };
GameaccountTypeApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: GameaccountTypeApiModule });
GameaccountTypeApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [GameaccountTypeResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(GameaccountTypeApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameaccountTypeApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [GameaccountTypeResource],
            }]
    }], null, null); })();

class PubgMatchResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Fetch the settings of a specific match.
     * Only works if the league has the pubg gameintegration enabled
     *
     * @param matchId
     */
    getSettingsOfMatch(matchId) {
        return this.http.get(`${this.config.giBaseUrl}/pubg/matches/${matchId}/settings`).pipe(immutable(), apiError());
    }
    /**
     * Fetch the meta data of a specific match.
     * Only works if the league has the pubg gameintegration enabled
     *
     * @param matchId
     */
    getMetaDataOfMatch(matchId) {
        return this.http.get(`${this.config.giBaseUrl}/pubg/matches/${matchId}/results`).pipe(immutable(), apiError());
    }
}
PubgMatchResource.ɵfac = function PubgMatchResource_Factory(t) { return new (t || PubgMatchResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
PubgMatchResource.ɵprov = i0.ɵɵdefineInjectable({ token: PubgMatchResource, factory: PubgMatchResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgMatchResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class PubgApiModule {
}
PubgApiModule.ɵfac = function PubgApiModule_Factory(t) { return new (t || PubgApiModule)(); };
PubgApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: PubgApiModule });
PubgApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [PubgMatchResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PubgApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [PubgMatchResource],
            }]
    }], null, null); })();

class FortniteMatchResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Fetch the meta data of a specific match.
     * Only works if the league has the pubg gameintegration enabled
     *
     * @param matchId
     */
    getMetaDataOfMatch(matchId) {
        return this.http.get(`${this.config.giBaseUrl}/fortnite/matches/${matchId}/results`).pipe(immutable(), apiError());
    }
}
FortniteMatchResource.ɵfac = function FortniteMatchResource_Factory(t) { return new (t || FortniteMatchResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
FortniteMatchResource.ɵprov = i0.ɵɵdefineInjectable({ token: FortniteMatchResource, factory: FortniteMatchResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FortniteMatchResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class FortniteApiModule {
}
FortniteApiModule.ɵfac = function FortniteApiModule_Factory(t) { return new (t || FortniteApiModule)(); };
FortniteApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: FortniteApiModule });
FortniteApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [FortniteMatchResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(FortniteApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FortniteApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [FortniteMatchResource],
            }]
    }], null, null); })();

class CocContestantResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Fetch the clan of a contestant in a league.
     * Only works if the league has the coc gameintegration enabled
     *
     * @param leagueId
     * @param contestantId
     */
    getClanOfContestant(leagueId, contestantId) {
        return this.http.get(`${this.config.giBaseUrl}/coc/leagues/${leagueId}/contestants/${contestantId}/clan`).pipe(immutable(), apiError());
    }
}
CocContestantResource.ɵfac = function CocContestantResource_Factory(t) { return new (t || CocContestantResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
CocContestantResource.ɵprov = i0.ɵɵdefineInjectable({ token: CocContestantResource, factory: CocContestantResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CocContestantResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class CocApiModule {
}
CocApiModule.ɵfac = function CocApiModule_Factory(t) { return new (t || CocApiModule)(); };
CocApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: CocApiModule });
CocApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [CocContestantResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CocApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CocApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [CocContestantResource],
            }]
    }], null, null); })();

class EseaResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Get the ESEA match for a match on Play.
     *
     * @param matchId
     */
    getEseaMatchForMatch(matchId) {
        return this.http.get(`${this.config.giBaseUrl}/esea/match/${matchId}`).pipe(immutable(), apiError());
    }
}
EseaResource.ɵfac = function EseaResource_Factory(t) { return new (t || EseaResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
EseaResource.ɵprov = i0.ɵɵdefineInjectable({ token: EseaResource, factory: EseaResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EseaResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class EseaApiModule {
}
EseaApiModule.ɵfac = function EseaApiModule_Factory(t) { return new (t || EseaApiModule)(); };
EseaApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: EseaApiModule });
EseaApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [EseaResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(EseaApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EseaApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [EseaResource],
            }]
    }], null, null); })();

class AutochessResource {
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    getAutochessMatch(matchId) {
        return this.http.get(`${this.config.giBaseUrl}/autochess/matches/${matchId}`).pipe(apiError());
    }
}
AutochessResource.ɵfac = function AutochessResource_Factory(t) { return new (t || AutochessResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
AutochessResource.ɵprov = i0.ɵɵdefineInjectable({ token: AutochessResource, factory: AutochessResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AutochessResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class AutochessModule {
}
AutochessModule.ɵfac = function AutochessModule_Factory(t) { return new (t || AutochessModule)(); };
AutochessModule.ɵmod = i0.ɵɵdefineNgModule({ type: AutochessModule });
AutochessModule.ɵinj = i0.ɵɵdefineInjector({ providers: [AutochessResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(AutochessModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AutochessModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [AutochessResource],
            }]
    }], null, null); })();

class PubgMobileMatchResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Sets a PUBG MOBILE gameaccount of the current logged in user
     *
     * @param nickname
     * @param uid
     */
    setPUBGMobileGameaccountOfUser(nickname, uid) {
        return this.http
            .post(`${this.config.apiBaseUrl}/pubgmobile/v1/user/gameaccounts`, { value: nickname, internalValue: uid })
            .pipe(immutable(), apiError());
    }
    /**
     * Fetch PUBG MOBILE room details (roomId === matchId)
     *
     * @param roomId
     */
    getPUBGMobileRoom(roomId) {
        return this.http.get(`${this.config.apiBaseUrl}/pubgmobile/v1/room/${roomId}`).pipe(immutable(), apiError());
    }
    /**
     * Fetch the meta data of a specific match.
     *
     * @param matchId
     */
    getMetaDataOfMatch(matchId) {
        return this.http.get(`${this.config.apiBaseUrl}/pubgmobile/v1/matches/${matchId}/results`).pipe(immutable(), apiError());
    }
}
PubgMobileMatchResource.ɵfac = function PubgMobileMatchResource_Factory(t) { return new (t || PubgMobileMatchResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
PubgMobileMatchResource.ɵprov = i0.ɵɵdefineInjectable({ token: PubgMobileMatchResource, factory: PubgMobileMatchResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgMobileMatchResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class PubgMobileApiModule {
}
PubgMobileApiModule.ɵfac = function PubgMobileApiModule_Factory(t) { return new (t || PubgMobileApiModule)(); };
PubgMobileApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: PubgMobileApiModule });
PubgMobileApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [PubgMobileMatchResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PubgMobileApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgMobileApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [PubgMobileMatchResource],
            }]
    }], null, null); })();

class GameintegrationApiModule {
}
GameintegrationApiModule.ɵfac = function GameintegrationApiModule_Factory(t) { return new (t || GameintegrationApiModule)(); };
GameintegrationApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: GameintegrationApiModule });
GameintegrationApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [], imports: [[
            CommonModule,
            HttpClientModule,
            PubgApiModule,
            CocApiModule,
            EseaApiModule,
            AutochessModule,
            FortniteApiModule,
            PubgMobileApiModule,
        ]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(GameintegrationApiModule, { imports: [CommonModule,
        HttpClientModule,
        PubgApiModule,
        CocApiModule,
        EseaApiModule,
        AutochessModule,
        FortniteApiModule,
        PubgMobileApiModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameintegrationApiModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    HttpClientModule,
                    PubgApiModule,
                    CocApiModule,
                    EseaApiModule,
                    AutochessModule,
                    FortniteApiModule,
                    PubgMobileApiModule,
                ],
                providers: [],
            }]
    }], null, null); })();

/**
 * Maps the LeagueQuery options to the HTTP parameters needed for the league API
 *
 * @param query
 */
function leagueQueryToSearch(query) {
    let httpParams = new HttpParams();
    if (!query) {
        return httpParams;
    }
    const params = {
        fields: query.fields ? query.fields.join(",") : null,
        types: query.types ? query.types.join(",") : null,
        states: query.states ? query.states.join(",") : null,
        portals: query.portals ? query.portals.join(",") : null,
        tags: query.tags ? query.tags.join(",") : null,
        countries: query.countries ? query.countries.join(",") : null,
        from: query.from,
        to: query.to,
        path: query.path,
        includeHidden: query.includeHidden,
        league_ids: query.leagueIds ? query.leagueIds.join(",") : null,
        game_ids: query.gameIds ? query.gameIds.join(",") : null,
        exclude_tags: query.excludeTags ? query.excludeTags.join(",") : null,
        team_sizes: query.teamSizes ? query.teamSizes.join(",") : null,
        skill_levels: query.skillLevels ? query.skillLevels.join(",") : null,
        "limit.total": query.limit ? query.limit.total : null,
        "limit.state.inProgress": query.limit ? query.limit.inProgress : null,
        "limit.state.upcoming": query.limit ? query.limit.upcoming : null,
        "limit.state.finished": query.limit ? query.limit.finished : null,
    };
    Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined) {
            httpParams = httpParams.set(key, params[key]);
        }
    });
    return httpParams;
}
class LeagueResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Fetches a league by it's Id
     *
     * @param leagueId
     */
    getLeague(leagueId) {
        return this.http.get(`${this.config.playBaseUrl}/leagues/${leagueId}`).pipe(immutable(), apiError());
    }
    /**
     * Fetches all leagues that matches the query
     *
     * @param query
     */
    queryLeagues(query) {
        if (query.leagueIds && !query.leagueIds.length) {
            return of([]);
        }
        return this.http
            .get(`${this.config.playBaseUrl}/leagues`, {
            params: leagueQueryToSearch(query),
        })
            .pipe(map(leagues => map$1(leagues, league => league)), // make it to array instead of object map
        immutable(), apiError());
    }
    /**
     * Fetches the league's namespace textblock
     *
     * @param leagueId
     * @param namespace
     */
    getLeagueNamespace(leagueId, namespace, language) {
        const httpParams = new HttpParams().set("language", language);
        return this.http
            .get(`${this.config.playBaseUrl}/leagues/${leagueId}/${namespace}`, {
            responseType: "text",
            params: httpParams,
        })
            .pipe(immutable(), apiError());
    }
    /**
     * Fetches the prizes of a league
     *
     * @param leagueId
     */
    getPrizesOfLeague(leagueId) {
        return this.http
            .get(`${this.config.playBaseUrl}/leagues/${leagueId}/prizes`, {
            responseType: "text",
        })
            .pipe(apiError());
    }
    /**
     * Fetches the matches of a league
     *
     * @param leagueId
     */
    getMatchesOfLeague(leagueId) {
        return this.http.get(`${this.config.playBaseUrl}/leagues/${leagueId}/matches`).pipe(immutable(), apiError());
    }
    /**
     * Fetches the matches of a league
     *
     * @param leagueId
     */
    getResultsOfLeague(leagueId) {
        return this.http.get(`${this.config.playBaseUrl}/leagues/${leagueId}/results`).pipe(immutable(), apiError());
    }
}
LeagueResource.ɵfac = function LeagueResource_Factory(t) { return new (t || LeagueResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
LeagueResource.ɵprov = i0.ɵɵdefineInjectable({ token: LeagueResource, factory: LeagueResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LeagueResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class LeagueApiModule {
}
LeagueApiModule.ɵfac = function LeagueApiModule_Factory(t) { return new (t || LeagueApiModule)(); };
LeagueApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: LeagueApiModule });
LeagueApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [LeagueResource], imports: [[CommonModule, HttpClientModule, ContestantApiModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(LeagueApiModule, { imports: [CommonModule, HttpClientModule, ContestantApiModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LeagueApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule, ContestantApiModule],
                providers: [LeagueResource],
            }]
    }], null, null); })();

/**
 * Maps the MatchQuery options to the HTTP parameters needed for the Match API
 *
 * @param query
 */
function matchQueryToSearch(query) {
    let httpParams = new HttpParams();
    if (!query) {
        return httpParams;
    }
    const params = {
        state: query.states ? query.states.join(",") : null,
        leagueIds: query.leagueIds ? query.leagueIds.join(",") : null,
        gameIds: query.gameIds ? query.gameIds.join(",") : null,
        limit: query.limit ? query.limit : null,
        fields: query.fields ? query.fields.join(",") : null,
    };
    Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined) {
            httpParams = httpParams.set(key, params[key]);
        }
    });
    return httpParams;
}
class MatchResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Fetch a match.
     *
     * @param matchId
     */
    getMatch(matchId) {
        return this.http.get(`${this.config.playBaseUrl}/matches/${matchId}`).pipe(immutable(), apiError());
    }
    /**
     * Fetches the uploaded media of players of a match
     *
     * @param matchId
     */
    getMatchmediaOfMatch(matchId) {
        return this.http.get(`${this.config.playBaseUrl}/matches/${matchId}/media`).pipe(immutable(), apiError());
    }
    /**
     * Reports a result of the full match played (all rounds)
     *
     * @param matchId
     * @param rounds
     * @param opponentAbsent
     * @param opponentCredibility Can be between 1-5. 1 for worst and 5 for best.
     */
    setResultOfMatch(matchId, rounds, opponentAbsent, opponentCredibility = 5) {
        // Force credibility to be 1-5
        const credibility = Math.max(1, Math.min(5, opponentCredibility));
        const resultDocument = {
            contestants: {
                opponent: {
                    absent: opponentAbsent,
                },
                self: {
                    credibility: credibility,
                },
            },
            rounds: rounds,
        };
        return this.http.post(`${this.config.playBaseUrl}/matches/${matchId}/result`, resultDocument).pipe(immutable(), apiError());
    }
    /**
     * Uploads match media to a match such as MOSS anticheat files or a scoreboard screenshot.
     * This will also report about the progress
     *
     * @param matchId
     * @param logo
     */
    uploadMatchmediaOfMatch(matchId, file) {
        const formData = new FormData();
        formData.append("title", file.name);
        formData.append("file", file, file.name);
        return this.http
            .post(`${this.config.playBaseUrl}/matches/${matchId}/media`, formData, {
            reportProgress: true,
            observe: "events",
        })
            .pipe(apiError());
    }
    /**
     * Returns the setup report of a match. This document indicates if the user's contestant has entered the challenge date or the opponent.
     *
     * @param matchId
     */
    getSetupReportOfMatch(matchId) {
        return this.http.get(`${this.config.playBaseUrl}/matches/${matchId}/setup`).pipe(immutable(), apiError());
    }
    /**
     * Returns the result report of a match. This document indicates if the user's contestant has entered the score or the opponent.
     *
     * @param matchId
     */
    getResultReportOfMatch(matchId) {
        return this.http.get(`${this.config.playBaseUrl}/matches/${matchId}/result`).pipe(immutable(), apiError());
    }
    /**
     * Returns the challenge report of a match. This document indicates if the user's contestant has initiated the challenge or the opponent.
     *
     * @param matchId
     */
    getChallengeReportOfMatch(matchId) {
        return this.http.get(`${this.config.playBaseUrl}/matches/${matchId}/challenge`).pipe(immutable(), apiError());
    }
}
MatchResource.ɵfac = function MatchResource_Factory(t) { return new (t || MatchResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
MatchResource.ɵprov = i0.ɵɵdefineInjectable({ token: MatchResource, factory: MatchResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class MatchApiModule {
}
MatchApiModule.ɵfac = function MatchApiModule_Factory(t) { return new (t || MatchApiModule)(); };
MatchApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: MatchApiModule });
MatchApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [MatchResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(MatchApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [MatchResource],
            }]
    }], null, null); })();

class ParticipantResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Fetches the league's participants
     *
     * @param leagueId
     */
    getParticipantsOfLeague(leagueId, states) {
        const httpParams = new HttpParams().set("states", states);
        return this.http
            .get(`${this.config.playBaseUrl}/leagues/${leagueId}/contestants`, {
            params: httpParams,
        })
            .pipe(immutable(), apiError());
    }
}
ParticipantResource.ɵfac = function ParticipantResource_Factory(t) { return new (t || ParticipantResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
ParticipantResource.ɵprov = i0.ɵɵdefineInjectable({ token: ParticipantResource, factory: ParticipantResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ParticipantResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class ParticipantApiModule {
}
ParticipantApiModule.ɵfac = function ParticipantApiModule_Factory(t) { return new (t || ParticipantApiModule)(); };
ParticipantApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: ParticipantApiModule });
ParticipantApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [ParticipantResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(ParticipantApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ParticipantApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [ParticipantResource],
            }]
    }], null, null); })();

/**
 * This function divides an array into an array of smaller arrays
 * @param array : array of any type ([4,5,6,2,3,4,8,9,6,4])
 * @param arrayLength : length of array chunck (2)
 * @returns : array of array chuncks ([[4,5],[6,2],[3,4],[4,8],[9,6],[4]])
 *
 */
const batches = (array, arrayLength) => {
    return array.reduce((resultArray, item, index) => {
        const chunkIndex = Math.floor(index / arrayLength);
        if (!resultArray[chunkIndex]) {
            resultArray[chunkIndex] = []; // start a new chunk
        }
        resultArray[chunkIndex].push(item);
        return resultArray;
    }, []);
};

class PlayerResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Sets the short description for the logged in player
     *
     * @param playerId
     * @param content
     */
    setShortDescriptionOfPlayer(playerId, content) {
        return this.http.post(`${this.config.playBaseUrl}/users/${playerId}/shortdescription`, content).pipe(apiError());
    }
    /**
     * Sets the description for the logged in player
     *
     * @param playerId
     * @param content
     */
    setDescriptionOfPlayer(playerId, content) {
        return this.http.post(`${this.config.playBaseUrl}/users/${playerId}/description`, content).pipe(apiError());
    }
    /**
     * Fetches the short description of a player
     *
     * @param playerId
     * @param format
     */
    getShortDescriptionOfPlayer(playerId, format) {
        const httpParams = new HttpParams().set("format", format);
        return this.http
            .get(`${this.config.playBaseUrl}/users/${playerId}/shortdescription`, {
            responseType: "text",
            params: httpParams,
        })
            .pipe(apiError());
    }
    /**
     * Fetches the description of a player
     *
     * @param playerId
     * @param format
     */
    getDescriptionOfPlayer(playerId, format) {
        const httpParams = new HttpParams().set("format", format);
        return this.http
            .get(`${this.config.playBaseUrl}/users/${playerId}/description`, {
            responseType: "text",
            params: httpParams,
        })
            .pipe(apiError());
    }
    /**
     * Fetches the ESL Play player profile of a given player id
     *
     * @param playerId
     */
    getPlayer(playerId) {
        return this.http.get(`${this.config.playBaseUrl}/users/${playerId}/basicprofile`).pipe(immutable(), apiError());
    }
    /**
     * Fetches the ESL Play player profiles of the given player ids
     *
     * @param playerId
     */
    getPlayers(playerIds) {
        const arrayOfObservables = batches(playerIds, 100).map(batch => {
            const httpParams = new HttpParams().set("ids", batch.join(","));
            return this.http
                .get(`${this.config.playBaseUrl}/users`, {
                params: httpParams,
            })
                .pipe(immutable(), apiError());
        });
        return combineLatest(arrayOfObservables).pipe(map(c => {
            return [].concat.apply([], c);
        }));
    }
    /**
     * Returns the users contestants for a league.
     *
     * @param leagueId
     */
    getContestantsOfPlayer(playerId, leagueId) {
        return this.http
            .get(`${this.config.playBaseUrl}/users/${playerId}/leagues/${leagueId}/contestants`)
            .pipe(immutable(), apiError());
    }
    /**
     * Fetches all the active gameaccounts of a player
     *
     * @param playerId
     */
    getGameaccountsOfPlayer(playerId) {
        return this.http.get(`${this.config.playBaseUrl}/users/${playerId}/gameaccounts`).pipe(immutable(), apiError());
    }
    /**
     * Fetches the active gameaccount of a player
     *
     * @param playerId
     * @param type
     */
    getGameaccountOfPlayer(playerId, type) {
        return this.http
            .get(`${this.config.playBaseUrl}/users/${playerId}/gameaccounts/${type}`)
            .pipe(immutable(), apiError());
    }
    /**
     * Fetches the player´s leagues
     *
     * @param playerId
     */
    getLeaguesOfPlayer(playerId, params) {
        const query = params ? { params: leagueQueryToSearch(params) } : {};
        return this.http
            .get(`${this.config.playBaseUrl}/users/${playerId}/leagues`, query)
            .pipe(immutable(), apiError());
    }
    /**
     * Fetches the player's matches
     *
     * @param playerId
     * @param matchQuery
     */
    getMatchesOfPlayer(playerId, matchQuery) {
        return this.http
            .get(`${this.config.playBaseUrl}/users/${playerId}/matches`, {
            params: matchQueryToSearch(matchQuery),
        })
            .pipe(immutable(), apiError());
    }
}
PlayerResource.ɵfac = function PlayerResource_Factory(t) { return new (t || PlayerResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
PlayerResource.ɵprov = i0.ɵɵdefineInjectable({ token: PlayerResource, factory: PlayerResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PlayerResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class TeamResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Sets the short description of a team.
     * Needs management permissions for that team.
     *
     * @param teamId
     * @param content
     */
    setShortDescriptionOfTeam(teamId, content) {
        return this.http.post(`${this.config.playBaseUrl}/teams/${teamId}/shortdescription`, content).pipe(apiError());
    }
    /**
     * Sets the description of a team.
     * Needs management permissions for that team.
     *
     * @param teamId
     * @param content
     */
    setDescriptionOfTeam(teamId, content) {
        return this.http.post(`${this.config.playBaseUrl}/teams/${teamId}/description`, content).pipe(apiError());
    }
    /**
     * Fetches the short description of a team.
     *
     * @param teamId
     * @param format
     */
    getShortDescriptionOfTeam(teamId, format) {
        const httpParams = new HttpParams().set("format", format);
        return this.http
            .get(`${this.config.playBaseUrl}/teams/${teamId}/shortdescription`, {
            responseType: "text",
            params: httpParams,
        })
            .pipe(apiError());
    }
    /**
     * Fetches the description of a team.
     *
     * @param teamId
     * @param format
     */
    getDescriptionOfTeam(teamId, format) {
        const httpParams = new HttpParams().set("format", format);
        return this.http
            .get(`${this.config.playBaseUrl}/teams/${teamId}/description`, {
            responseType: "text",
            params: httpParams,
        })
            .pipe(apiError());
    }
    /**
     * Creates and returns the freshly created team.
     * If no password is specified, the system will generate a random one.
     *
     * @param name
     * @param password
     */
    createTeam(name, password) {
        return this.http
            .post(`${this.config.playBaseUrl}/user/teams`, {
            name: name,
            password: password,
        })
            .pipe(immutable(), apiError());
    }
    /**
     * Joins a team if the password is correct.
     * No password needed for public teams.
     *
     * @param teamId
     * @param password
     */
    joinTeam(teamId, password) {
        return this.http
            .post(`${this.config.playBaseUrl}/teams/${teamId}/members`, {
            password: password,
        })
            .pipe(immutable(), apiError());
    }
    /**
     * Edits a team setting (name, password, etc.)
     * Needs management permissions for that team.
     *
     * @param teamId
     * @param fields
     */
    editTeam(teamId, fields) {
        return this.http.put(`${this.config.playBaseUrl}/teams/${teamId}`, fields).pipe(immutable(), apiError());
    }
    /**
     * Deletes a team and thus forfeits all matches.
     * Needs leader permissions for that team.
     *
     * @param teamId
     */
    deleteTeam(teamId) {
        return this.http.delete(`${this.config.playBaseUrl}/teams/${teamId}`).pipe(apiError());
    }
    /**
     * Fetches a team.
     * Will include the password if management permissions are available.
     *
     * @param teamId
     */
    getTeam(teamId) {
        return this.http.get(`${this.config.playBaseUrl}/teams/${teamId}`).pipe(immutable(), apiError());
    }
    /**
     * Fetches multiple teams.
     * Will include the password if management permissions are available.
     *
     * @param teamId
     */
    getTeams(teamId) {
        const arrayOfObservables = batches(teamId, 100).map(batch => {
            const httpParams = new HttpParams().set("ids", batch.join(","));
            return this.http
                .get(`${this.config.playBaseUrl}/teams`, {
                params: httpParams,
            })
                .pipe(immutable(), apiError());
        });
        return combineLatest(arrayOfObservables).pipe(map(c => {
            return [].concat.apply([], c);
        }));
    }
    /**
     * Fetches a team's members.
     *
     * @param teamId
     */
    getMembersOfTeam(teamId) {
        return this.http.get(`${this.config.playBaseUrl}/teams/${teamId}/members`).pipe(map(response => map$1(response, member => member)), // make it to array instead of object map.pipe(
        immutable(), apiError());
    }
    /**
     * Kicks a member from the team.
     * Needs management permissions (or leader, depending on member that should be kicked) for that team.
     *
     * @param teamId
     * @param memberId
     */
    kickTeamMember(teamId, memberId) {
        return this.http.delete(`${this.config.playBaseUrl}/teams/${teamId}/members/${memberId}`).pipe(apiError());
    }
    /**
     * Edit a members permissions and/or role of the team.
     * Needs management permissions (or leader, depending on member that should be edited) for that team.
     *
     * @param teamId
     * @param memberId
     * @param membership
     */
    editTeamMember(teamId, memberId, membership) {
        return this.http
            .put(`${this.config.playBaseUrl}/teams/${teamId}/members/${memberId}/membership`, membership)
            .pipe(immutable(), apiError());
    }
    /**
     * Invites a new player by id to the team.
     * Needs management permissions for that team.
     *
     * @param teamId
     * @param playerId
     * @param domain
     */
    invitePlayerByIdToTeam(teamId, playerId, domain) {
        const fields = {
            domain: domain,
            userId: playerId,
        };
        return this.http.post(`${this.config.playBaseUrl}/teams/${teamId}/invitation`, fields).pipe(apiError());
    }
    /**
     * Invites a new player by email to the team.
     * Needs management permissions for that team.
     *
     * @param teamId
     * @param email
     * @param domain
     */
    invitePlayerByEmailToTeam(teamId, email, domain) {
        const fields = {
            domain: domain,
            email: email,
        };
        return this.http.post(`${this.config.playBaseUrl}/teams/${teamId}/invitation`, fields).pipe(apiError());
    }
    /**
     * Uploads a new logo for the specified team.
     * This will also report about the progress
     *
     * @param teamId
     * @param logo
     */
    uploadLogoOfTeam(teamId, logo) {
        const formData = new FormData();
        formData.append("logo", logo);
        return this.http
            .post(`${this.config.playBaseUrl}/teams/${teamId}/logo`, formData, {
            reportProgress: true,
            observe: "events",
        })
            .pipe(apiError());
    }
}
TeamResource.ɵfac = function TeamResource_Factory(t) { return new (t || TeamResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
TeamResource.ɵprov = i0.ɵɵdefineInjectable({ token: TeamResource, factory: TeamResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TeamResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class TeamApiModule {
}
TeamApiModule.ɵfac = function TeamApiModule_Factory(t) { return new (t || TeamApiModule)(); };
TeamApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: TeamApiModule });
TeamApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [TeamResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(TeamApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TeamApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [TeamResource],
            }]
    }], null, null); })();

class PlayerApiModule {
}
PlayerApiModule.ɵfac = function PlayerApiModule_Factory(t) { return new (t || PlayerApiModule)(); };
PlayerApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: PlayerApiModule });
PlayerApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [PlayerResource], imports: [[CommonModule, HttpClientModule, TeamApiModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PlayerApiModule, { imports: [CommonModule, HttpClientModule, TeamApiModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PlayerApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule, TeamApiModule],
                providers: [PlayerResource],
            }]
    }], null, null); })();

class PremiumResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Get the Xsolla Access Token
     */
    getAccessToken(returnUrl, digitalContent) {
        return this.http
            .post(`${this.config.playBaseUrl}/user/premium/xsolla?returnUrl=${encodeURI(returnUrl)}${digitalContent ? `&digitalContent=${digitalContent}` : ""}`, null, { responseType: "text" })
            .pipe(apiError());
    }
}
PremiumResource.ɵfac = function PremiumResource_Factory(t) { return new (t || PremiumResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
PremiumResource.ɵprov = i0.ɵɵdefineInjectable({ token: PremiumResource, factory: PremiumResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PremiumResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class PremiumApiModule {
}
PremiumApiModule.ɵfac = function PremiumApiModule_Factory(t) { return new (t || PremiumApiModule)(); };
PremiumApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: PremiumApiModule });
PremiumApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [PremiumResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PremiumApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PremiumApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [PremiumResource],
            }]
    }], null, null); })();

/**
 * Maps the RankingQuery options to the HTTP parameters needed for the Ranking API
 *
 * @param query
 */
function rankingQueryToSearch(query) {
    let httpParams = new HttpParams();
    if (!query) {
        return httpParams;
    }
    const params = {
        contestantId: query.contestantId ? query.contestantId : null,
        limit: query.limit ? query.limit : null,
        offset: query.offset ? query.offset : null,
    };
    Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined) {
            httpParams = httpParams.set(key, params[key]);
        }
    });
    return httpParams;
}
class RankingResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Fetch the ranking of a league
     *
     * @param leagueId
     * @param query
     */
    getRankingOfLeague(leagueId, query) {
        return this.http
            .get(`${this.config.playBaseUrl}/leagues/${leagueId}/ranking`, {
            params: rankingQueryToSearch(query),
        })
            .pipe(immutable(), apiError());
    }
    /**
     * Fetches the ranking of a specific group in an FFA tournament
     *
     * @param leagueId
     * @param round
     * @param group
     */
    getRankingOfFFAGroupOfLeague(leagueId, round, group) {
        const httpParams = !isUndefined(round) && !isUndefined(group)
            ? {
                params: new HttpParams().set("round", round.toString()).set("group", group.toString()),
            }
            : {};
        return this.http.get(`${this.config.playBaseUrl}/leagues/${leagueId}/ranking`, httpParams).pipe(immutable(), apiError());
    }
}
RankingResource.ɵfac = function RankingResource_Factory(t) { return new (t || RankingResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
RankingResource.ɵprov = i0.ɵɵdefineInjectable({ token: RankingResource, factory: RankingResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RankingResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class RankingApiModule {
}
RankingApiModule.ɵfac = function RankingApiModule_Factory(t) { return new (t || RankingApiModule)(); };
RankingApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: RankingApiModule });
RankingApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [RankingResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(RankingApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RankingApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [RankingResource],
            }]
    }], null, null); })();

class RestrictionResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Fetches the league restrictions of a league & the status for a specified contestant.
     *
     * @param leagueId
     * @param contestantId
     */
    getRestrictionsOfLeague(leagueId, contestantId) {
        const httpParams = new HttpParams().set("contestantId", contestantId.toString());
        return this.http
            .get(`${this.config.playBaseUrl}/leagues/${leagueId}/signup`, {
            params: httpParams,
        })
            .pipe(immutable(), apiError());
    }
}
RestrictionResource.ɵfac = function RestrictionResource_Factory(t) { return new (t || RestrictionResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
RestrictionResource.ɵprov = i0.ɵɵdefineInjectable({ token: RestrictionResource, factory: RestrictionResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RestrictionResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class RestrictionApiModule {
}
RestrictionApiModule.ɵfac = function RestrictionApiModule_Factory(t) { return new (t || RestrictionApiModule)(); };
RestrictionApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: RestrictionApiModule });
RestrictionApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [RestrictionResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(RestrictionApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RestrictionApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [RestrictionResource],
            }]
    }], null, null); })();

class RosterResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Fetches a roster.
     *
     * @param leagueId
     * @param teamId
     */
    getRoster(leagueId, teamId) {
        return this.http.get(`${this.config.playBaseUrl}/leagues/${leagueId}/rosters/${teamId}`).pipe(immutable(), apiError());
    }
    /**
     * Fetches a rosters event history.
     * E.g. leaving or joining a roster.
     *
     * @param leagueId
     * @param teamId
     */
    getRosterHistory(leagueId, teamId) {
        return this.http.get(`${this.config.playBaseUrl}/leagues/${leagueId}/rosters/${teamId}/history`).pipe(immutable(), apiError());
    }
    /**
     * Adds a single player to a roster.
     *
     * @param leagueId
     * @param teamId
     * @param playerId
     */
    addPlayer(leagueId, teamId, playerId) {
        return this.http.put(`${this.config.playBaseUrl}/leagues/${leagueId}/rosters/${teamId}/members/${playerId}`, {}).pipe(apiError());
    }
    /**
     * Removes a single player from a roster.
     *
     * @param leagueId
     * @param teamId
     * @param playerId
     */
    removePlayer(leagueId, teamId, playerId) {
        return this.http.delete(`${this.config.playBaseUrl}/leagues/${leagueId}/rosters/${teamId}/members/${playerId}`).pipe(apiError());
    }
}
RosterResource.ɵfac = function RosterResource_Factory(t) { return new (t || RosterResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
RosterResource.ɵprov = i0.ɵɵdefineInjectable({ token: RosterResource, factory: RosterResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RosterResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class RosterApiModule {
}
RosterApiModule.ɵfac = function RosterApiModule_Factory(t) { return new (t || RosterApiModule)(); };
RosterApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: RosterApiModule });
RosterApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [RosterResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(RosterApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RosterApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [RosterResource],
            }]
    }], null, null); })();

class UserResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Returns the days left of premium (PD) of the current logged in user
     *
     * @returns returns a number if the user has premium, else null
     */
    getPremiumDaysOfUser() {
        return this.http.get(`${this.config.playBaseUrl}/user/premium`).pipe(immutable(), apiError());
    }
    /**
     * Fetches the ESL profile of the current logged in user.
     */
    getUser() {
        return this.http.get(`${this.config.playBaseUrl}/user/profile`).pipe(immutable(), apiError());
    }
    /**
     * Fetches all teams of the current logged in user.
     */
    getTeamsOfUser() {
        return this.http.get(`${this.config.playBaseUrl}/user/teams`).pipe(map(teams => map$1(teams, team => team)), // make it to array instead of object map
        immutable(), apiError());
    }
    /**
     * Sets a gameaccount of the current logged in user
     *
     * @param gameaccountType
     * @param gameaccount
     */
    setGameaccountOfUser(gameaccountType, gameaccount) {
        return this.http.post(`${this.config.playBaseUrl}/user/gameaccounts/${gameaccountType}`, { value: gameaccount }).pipe(immutable(), apiError());
    }
    /**
     * Sets user profile data of current logged in user
     *
     * @param userUpdate
     */
    updateProfileOfUser(userUpdate) {
        return this.http.put(`${this.config.playBaseUrl}/user/profile`, userUpdate).pipe(apiError());
    }
    /**
     * Updates the short description for the logged in player
     *
     * @param content
     */
    updateShortDescriptionOfUser(content) {
        return this.http
            .put(`${this.config.playBaseUrl}/user/profile/shortdescription`, { shortDescription: content })
            .pipe(apiError());
    }
    /**
     * Updates user photo of current logged in user
     *
     * @param photo
     */
    uploadPhotoOfUser(photo) {
        const formData = new FormData();
        formData.append("file", photo);
        return this.http
            .post(`${this.config.playBaseUrl}/user/profile/picture`, formData, {
            reportProgress: true,
            observe: "events",
        })
            .pipe(apiError());
    }
    /**
     * Fetches Shopify discount codes for user
     */
    createShopDiscountCodesForUser() {
        return this.http.post(`${this.config.playBaseUrl}/user/premium/shopify`, null).pipe(apiError());
    }
    /**
     * Gets suggested regions for the user
     */
    getUserRegions() {
        return this.http.get("https://play.eslgaming.com/user/regions").pipe(immutable(), apiError());
    }
}
UserResource.ɵfac = function UserResource_Factory(t) { return new (t || UserResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
UserResource.ɵprov = i0.ɵɵdefineInjectable({ token: UserResource, factory: UserResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class UserApiModule {
}
UserApiModule.ɵfac = function UserApiModule_Factory(t) { return new (t || UserApiModule)(); };
UserApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: UserApiModule });
UserApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [UserResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(UserApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [UserResource],
            }]
    }], null, null); })();

class MetaRankResource {
    /**
     * @param http
     * @param config
     */
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    /**
     * Returns the ranking for a specific series and season
     */
    getRankingForSeries(series, season, limit, offset) {
        let httpParams = new HttpParams();
        if (limit || offset) {
            const params = {
                limit,
                offset,
            };
            Object.keys(params).forEach(key => {
                if (params[key]) {
                    httpParams = httpParams.set(key, params[key]);
                }
            });
        }
        return this.http
            .get(`${this.config.metarankBaseUrl}/ranking/${series}/${season}`, {
            params: httpParams,
        })
            .pipe(immutable(), apiError());
    }
}
MetaRankResource.ɵfac = function MetaRankResource_Factory(t) { return new (t || MetaRankResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(API_CONFIG)); };
MetaRankResource.ɵprov = i0.ɵɵdefineInjectable({ token: MetaRankResource, factory: MetaRankResource.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MetaRankResource, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [API_CONFIG]
            }] }]; }, null); })();

class MetaRankApiModule {
}
MetaRankApiModule.ɵfac = function MetaRankApiModule_Factory(t) { return new (t || MetaRankApiModule)(); };
MetaRankApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: MetaRankApiModule });
MetaRankApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [MetaRankResource], imports: [[CommonModule, HttpClientModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(MetaRankApiModule, { imports: [CommonModule, HttpClientModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MetaRankApiModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, HttpClientModule],
                providers: [MetaRankResource],
            }]
    }], null, null); })();

class TournamentApiModule {
}
TournamentApiModule.ɵfac = function TournamentApiModule_Factory(t) { return new (t || TournamentApiModule)(); };
TournamentApiModule.ɵmod = i0.ɵɵdefineNgModule({ type: TournamentApiModule });
TournamentApiModule.ɵinj = i0.ɵɵdefineInjector({ providers: [{ provide: API_CONFIG, useValue: apiBaseConfig }], imports: [[
            CommonModule,
            HttpClientModule,
            ContestantApiModule,
            GameApiModule,
            GameaccountTypeApiModule,
            LeagueApiModule,
            MatchApiModule,
            ParticipantApiModule,
            PlayerApiModule,
            PremiumApiModule,
            RankingApiModule,
            RestrictionApiModule,
            RosterApiModule,
            TeamApiModule,
            UserApiModule,
            GameintegrationApiModule,
            MetaRankApiModule,
        ]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(TournamentApiModule, { imports: [CommonModule,
        HttpClientModule,
        ContestantApiModule,
        GameApiModule,
        GameaccountTypeApiModule,
        LeagueApiModule,
        MatchApiModule,
        ParticipantApiModule,
        PlayerApiModule,
        PremiumApiModule,
        RankingApiModule,
        RestrictionApiModule,
        RosterApiModule,
        TeamApiModule,
        UserApiModule,
        GameintegrationApiModule,
        MetaRankApiModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TournamentApiModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    HttpClientModule,
                    ContestantApiModule,
                    GameApiModule,
                    GameaccountTypeApiModule,
                    LeagueApiModule,
                    MatchApiModule,
                    ParticipantApiModule,
                    PlayerApiModule,
                    PremiumApiModule,
                    RankingApiModule,
                    RestrictionApiModule,
                    RosterApiModule,
                    TeamApiModule,
                    UserApiModule,
                    GameintegrationApiModule,
                    MetaRankApiModule,
                ],
                providers: [{ provide: API_CONFIG, useValue: apiBaseConfig }],
            }]
    }], null, null); })();

/*
 * Public API Surface of api-client
 */

/**
 * Generated bundle index. Do not edit.
 */

export { API_CONFIG, ApiError, AuthenticationError, AuthorizationError, AutochessModule, AutochessResource, BadRequestError, CocApiModule, CocContestantResource, ContestantApiModule, ContestantResource, EseaApiModule, EseaResource, FortniteApiModule, FortniteMatchResource, GameApiModule, GameResource, GameaccountTypeApiModule, GameaccountTypeResource, GameintegrationApiModule, InternalServerError, LeagueApiModule, LeagueResource, MaintenanceError, MatchApiModule, MatchResource, MetaRankApiModule, MetaRankResource, NotFoundError, ParticipantApiModule, ParticipantResource, PlayerApiModule, PlayerResource, PremiumApiModule, PremiumResource, PubgApiModule, PubgMatchResource, PubgMobileApiModule, PubgMobileMatchResource, RankingApiModule, RankingResource, RestrictionApiModule, RestrictionResource, RosterApiModule, RosterResource, TeamApiModule, TeamResource, TournamentApiModule, UserApiModule, UserResource, apiBaseConfig, apiError, immutable };
//# sourceMappingURL=play-api-client.js.map
